import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Box, Button, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead,
  TablePagination, TableRow,
} from '@mui/material';
import classNames from 'classnames/bind';
import { AutoAvatar } from '../../../components/autoAvatar/AutoAvatar';
import { AvatarFromAws } from '../../../components/avatarFromAws/AvatarFromAws';
import { KebabMenu } from '../../../components/kebabMenu/KebabMenu';
import { ModalVolunteerDetails } from '../../../components/modals/modalVolunteerDetails/ModalVolunteerDetails';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { getVolunteers } from '../../../store/actions/actions';
import { routesNames, lackData } from '../../../utils/constants';
import { toHumanDate, toMonthFormat } from '../../../utils/data';
import { toCapitalizeCase } from '../../../utils/methods';
import { IVolunteer } from './types';
import { useStyles } from './volunteers.style';
import { SocketContext } from '../../../contexts/SocketContext';
import { messengerEvents } from '../../../websocket/events';

export const Volunteers = (): JSX.Element => {
  const opportunities = useAppSelector((state) => state.organizerReducer.opportunitiesById);
  const volunteers = useAppSelector((state) => state.organizerReducer.volunteers);
  const volunteerAmount = useAppSelector((state) => state.organizerReducer.volunteerAmount);
  const searchString = new URLSearchParams(window.location.search);
  const id = searchString.get('id');
  const [page, setPage] = useState(0);
  const [volunteer, setVolunteer] = useState<IVolunteer | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const classes = useStyles();
  const cx = classNames.bind(classes);

  const isOneDayOpp = !!(opportunities?.frequency[0] === 'ONE_DAY');

  useEffect(() => {
    getVolunteers(id)(dispatch);
  }, []);

  const handleChangePage = (event: unknown, newPage: number): void => {
    setPage(newPage);
    getVolunteers(id, newPage * rowsPerPage, rowsPerPage)(dispatch);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(+event.target.value);
    setPage(0);
    getVolunteers(id, 0, +event.target.value)(dispatch);
  };

  const paginationProps = {
    component: 'div',
    rowsPerPage,
    page,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [10, 25, 100],
  };

  const handleClick = (event: any, item: any) => {
    if (!event.target.closest('button')) {
      setVolunteer(item);
      navigate(`${routesNames.details}?id=${id}&volunteerId=${item.id}`);
    }
  };

  const avatarClassName = (i: number) => cx({
    firstAvatarPosition: i === 1,
    secondAvatarPosition: i === 2,
  });

  const avatarBlockClassName = cx({
    avatarBlock1: volunteerAmount <= 1,
    avatarBlock2: volunteerAmount === 2,
    avatarBlock3: volunteerAmount >= 3,
  });

  const volunteerClassName = cx({
    volunteers1: volunteerAmount <= 1,
    volunteers2: volunteerAmount === 2,
    volunteers3: volunteerAmount >= 3,
  });

  const avatarList = (i: number, item: any): JSX.Element | undefined => {
    if (i < 3) {
      if (item.photoKey && item.photoKey !== 'no_key') {
        return <AvatarFromAws photoKey={item.photoKey} className={`${classes.avatarList} ${avatarClassName(i)}`} />;
      }
      return <AutoAvatar name={`${item.firstName} ${item.lastName}`} className={avatarClassName(i)} />;
    }
    return undefined;
  };

  const { sendMessage } = useContext(SocketContext);

  const getVolunteerAttendingDate = (volunteerId: string) => {
    const dateString = opportunities?.bookedDates?.find((booked: any) => booked.volunteerId === volunteerId)?.date;
    return typeof dateString === 'string' ? toHumanDate(dateString, 'd MMMM yyyy') : '-';
  };

  return (
    <Box className={classes.volunteersBlock}>
      <div className={classes.noticeBlock}>
        <h3 className={classes.title}>Volunteers</h3>
        <div className={classes.buttonBlock}>
          <Button
            className={classes.buttonEdit}
            onClick={() => {
              sendMessage(JSON.stringify({ event: messengerEvents.getChats }));
              navigate(`${routesNames.notice}?id=${id}`);
            }}
          >MESSAGE VOLUNTEERS
          </Button>
        </div>
      </div>
      <div className={volunteerClassName}>
        {volunteerAmount > 0 && (
          <div className={avatarBlockClassName}>
            {
              volunteers?.map((item: IVolunteer, i: number) => (
                avatarList(i, item)
              ))
            }
          </div>
        )}
        <p className={classes.paragraph}>{`${volunteerAmount || 0} of ${opportunities?.maximumVolunteers} volunteers participate`}</p>
      </div>
      <div className={classes.paperBlock}>
        <Paper className={classes.paper}>
          <TableContainer className={classes.tableContainer}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead sx = {{ fontWeight: '900' }} >
                <TableRow className={classes.tableHead}>
                  <TableCell>
                  </TableCell>
                  <TableCell>
                    Full name
                  </TableCell>
                  <TableCell>
                    Status
                  </TableCell>
                  <TableCell>
                    Gender
                  </TableCell>
                  <TableCell>
                    Date of birth
                  </TableCell>
                  <TableCell>
                    Phone number
                  </TableCell>
                  <TableCell>
                    Contact email
                  </TableCell>
                  <TableCell>
                    Company
                  </TableCell>
                  <TableCell>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  volunteers.length ? volunteers.map((item: IVolunteer) => (
                    <TableRow
                      key={item.id}
                      tabIndex={-1}
                      role="checkbox"
                      className={classes.tableRow}
                      onClick={(e) => handleClick(e, item)}
                    >
                      <TableCell>
                        {
                          item.photoKey && item.photoKey !== 'no_key'
                            ? <AvatarFromAws photoKey={item.photoKey} className={classes.avatar} />
                            : <AutoAvatar name={`${item.firstName} ${item.lastName}`} />
                        }
                      </TableCell>
                      <TableCell>
                        {`${item.firstName} ${item.lastName}`}
                      </TableCell>
                      <TableCell>
                        {toCapitalizeCase(item.status)}
                      </TableCell>
                      <TableCell>
                        {toCapitalizeCase(item.gender)}
                      </TableCell>
                      <TableCell>
                        {toMonthFormat('D MMMM YYYY', item.dateOfBirth)}
                      </TableCell>
                      <TableCell>
                        {item.phoneNumber}
                      </TableCell>
                      <TableCell
                        onClick={() => navigator.clipboard.writeText(item.contactEmail)}
                        className={classes.copyText}
                      >
                        {item.contactEmail}
                      </TableCell>
                      <TableCell>
                        {item.company}
                      </TableCell>
                      <TableCell>
                        <KebabMenu volunteerId={item.id} />
                      </TableCell>
                    </TableRow>
                  )) : <div>{lackData.volunteers}</div>}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            {...paginationProps}
            count={volunteerAmount || 0}
          />
        </Paper>
      </div>
      <ModalVolunteerDetails
        open={!!volunteer}
        setOpen={setVolunteer}
        volunteer={volunteer}
      />
    </Box>
  );
};
